// Summary UI shared by the Ocean (OMBL), Air (AMBL) and Road (RMBL) master file pages:
// a KPI strip and the Financial Summary panel (cost lines, selling - cost = profit).
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

export const FIN_UI = {
    text: "#0f172a",
    muted: "#64748b",
    border: "#e2e8f0",
    track: "#f1f5f9",
    accent: "#2563eb",
    tone: {
        success: { fg: "#15803d", bg: "#f0fdf4", bar: "#22c55e" },
        warning: { fg: "#b45309", bg: "#fffbeb", bar: "#f59e0b" },
        error: { fg: "#b91c1c", bg: "#fef2f2", bar: "#ef4444" },
    },
};

const toNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

export const profitTone = (value) => {
    const n = toNumber(value);
    if (n > 0) return "success";
    if (n === 0) return "warning";
    return "error";
};

export const profitLabel = (value) => {
    const n = toNumber(value);
    if (n > 0) return "Profitable";
    if (n === 0) return "Break-even";
    return "Loss";
};

export const formatMoney = (value, currency) =>
    `${toNumber(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency || ""}`.trim();

export const formatQuantity = (value, digits = 2) =>
    toNumber(value).toLocaleString("en-US", { maximumFractionDigits: digits });

export const formatPercent = (value) => `${toNumber(value).toFixed(1)}%`;

// Each KPI tile draws its right and bottom divider; the strip clips the ones on its outer
// edge, so the lines stay correct however many tiles fit on a row.
export function KpiStrip({ children, sx }) {
    return (
        <MDBox
            sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(175px, 1fr))",
                backgroundColor: "#fff",
                border: `1px solid ${FIN_UI.border}`,
                borderRadius: "14px",
                overflow: "hidden",
                boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                ...sx,
            }}
        >
            {children}
        </MDBox>
    );
}

export function FinanceKpi({ label, value, hint, toneKey }) {
    const tone = toneKey ? FIN_UI.tone[toneKey] : null;
    return (
        <MDBox sx={{ px: 2.25, py: 2, boxShadow: `1px 0 0 ${FIN_UI.border}, 0 1px 0 ${FIN_UI.border}` }}>
            <MDTypography
                sx={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: FIN_UI.muted }}
            >
                {label}
            </MDTypography>
            <MDTypography
                sx={{
                    // Shrinks a little on narrower screens so amounts like "36,987.00 EUR" stay on one line.
                    fontSize: "clamp(18px, 1.1vw + 7px, 22px)",
                    fontWeight: 700,
                    mt: 0.5,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: tone ? tone.fg : FIN_UI.text,
                    fontVariantNumeric: "tabular-nums",
                    lineHeight: 1.2,
                }}
            >
                {value}
            </MDTypography>
            {hint ? (
                <MDBox mt={0.75} sx={{ fontSize: 12, color: FIN_UI.muted }}>
                    {hint}
                </MDBox>
            ) : null}
        </MDBox>
    );
}

export function ProfitChip({ profit }) {
    const tone = FIN_UI.tone[profitTone(profit)];
    return (
        <Chip
            size="small"
            label={profitLabel(profit)}
            sx={{ height: 22, fontSize: 11, fontWeight: 700, color: tone.fg, backgroundColor: tone.bg, border: `1px solid ${tone.bar}40` }}
        />
    );
}

// costLines: [{ label, value }] in display currency; cost is the total they add up to on the page.
export function FinancialSummaryPanel({ costLines, cost, selling, profit, margin, currency, costLabel = "Total Cost (Buying Rate)" }) {
    const toneKey = profitTone(profit);
    const tone = FIN_UI.tone[toneKey];
    const linesTotal = costLines.reduce((sum, line) => sum + toNumber(line.value), 0);
    const marginWidth = Math.max(0, Math.min(100, toNumber(margin)));

    const panelSx = { border: `1px solid ${FIN_UI.border}`, borderRadius: "14px", backgroundColor: "#fff" };
    const panelTitle = (title, right) => (
        <MDBox display="flex" justifyContent="space-between" alignItems="center" px={2.5} py={1.75} sx={{ borderBottom: `1px solid ${FIN_UI.border}` }}>
            <MDTypography sx={{ fontSize: 13, fontWeight: 700, color: FIN_UI.text }}>{title}</MDTypography>
            {right}
        </MDBox>
    );
    const amountSx = { fontSize: 13, fontWeight: 600, color: FIN_UI.text, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };

    return (
        <MDBox>
            <KpiStrip sx={{ mb: 2, boxShadow: "none" }}>
                <FinanceKpi label="Total Cost" value={formatMoney(cost, currency)} hint="Sum of all cost lines" />
                <FinanceKpi label="Total Selling" value={formatMoney(selling, currency)} hint="Selling rate of the linked files" />
                <FinanceKpi label="Gross Profit" value={formatMoney(profit, currency)} toneKey={toneKey} hint={<ProfitChip profit={profit} />} />
                <FinanceKpi label="Margin" value={formatPercent(margin)} toneKey={toneKey} hint="Gross profit / total selling" />
            </KpiStrip>

            <Grid container spacing={2}>
                <Grid item xs={12} md={7}>
                    <MDBox sx={{ ...panelSx, height: "100%" }}>
                        {panelTitle(
                            "Cost Breakdown",
                            <MDTypography sx={{ fontSize: 12, color: FIN_UI.muted }}>Share of total cost</MDTypography>
                        )}
                        <MDBox px={2.5} py={1}>
                            {costLines.map((line) => {
                                const value = toNumber(line.value);
                                const share = linesTotal > 0 ? (value / linesTotal) * 100 : 0;
                                return (
                                    <MDBox
                                        key={line.label}
                                        display="grid"
                                        alignItems="center"
                                        columnGap={2}
                                        py={1}
                                        sx={{ gridTemplateColumns: "minmax(90px, 140px) 1fr 44px auto", borderBottom: `1px dashed ${FIN_UI.border}` }}
                                    >
                                        <MDTypography sx={{ fontSize: 13, color: value ? FIN_UI.text : FIN_UI.muted }}>
                                            {line.label}
                                        </MDTypography>
                                        <MDBox sx={{ height: 6, borderRadius: 3, backgroundColor: FIN_UI.track, overflow: "hidden" }}>
                                            <MDBox sx={{ width: `${share}%`, height: "100%", backgroundColor: FIN_UI.accent, borderRadius: 3 }} />
                                        </MDBox>
                                        <MDTypography sx={{ fontSize: 12, color: FIN_UI.muted, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                                            {share ? `${share.toFixed(0)}%` : "-"}
                                        </MDTypography>
                                        <MDTypography sx={{ ...amountSx, textAlign: "right", color: value ? FIN_UI.text : FIN_UI.muted, fontWeight: value ? 600 : 400 }}>
                                            {formatMoney(value, currency)}
                                        </MDTypography>
                                    </MDBox>
                                );
                            })}
                            <MDBox display="flex" justifyContent="space-between" pt={1.5} pb={0.5}>
                                <MDTypography sx={{ fontSize: 13, fontWeight: 700, color: FIN_UI.text }}>{costLabel}</MDTypography>
                                <MDTypography sx={{ ...amountSx, fontWeight: 700 }}>{formatMoney(cost, currency)}</MDTypography>
                            </MDBox>
                        </MDBox>
                    </MDBox>
                </Grid>

                <Grid item xs={12} md={5}>
                    <MDBox sx={{ ...panelSx, height: "100%", display: "flex", flexDirection: "column" }}>
                        {panelTitle("Profitability", <ProfitChip profit={profit} />)}
                        <MDBox px={2.5} py={1.5} flex={1} display="flex" flexDirection="column">
                            <MDBox display="flex" justifyContent="space-between" py={0.75}>
                                <MDTypography sx={{ fontSize: 13, color: FIN_UI.muted }}>Total Selling</MDTypography>
                                <MDTypography sx={amountSx}>{formatMoney(selling, currency)}</MDTypography>
                            </MDBox>
                            <MDBox display="flex" justifyContent="space-between" py={0.75}>
                                <MDTypography sx={{ fontSize: 13, color: FIN_UI.muted }}>Total Cost</MDTypography>
                                <MDTypography sx={amountSx}>− {formatMoney(cost, currency)}</MDTypography>
                            </MDBox>
                            <MDBox
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                mt={1}
                                px={1.5}
                                py={1.25}
                                sx={{ borderRadius: "10px", backgroundColor: tone.bg }}
                            >
                                <MDTypography sx={{ fontSize: 13, fontWeight: 700, color: tone.fg }}>Gross Profit</MDTypography>
                                <MDTypography sx={{ ...amountSx, fontSize: 16, fontWeight: 700, color: tone.fg }}>
                                    {formatMoney(profit, currency)}
                                </MDTypography>
                            </MDBox>

                            <MDBox mt="auto" pt={2}>
                                <MDBox display="flex" justifyContent="space-between" mb={0.75}>
                                    <MDTypography sx={{ fontSize: 12, color: FIN_UI.muted }}>Profit margin</MDTypography>
                                    <MDTypography sx={{ fontSize: 12, fontWeight: 700, color: tone.fg, fontVariantNumeric: "tabular-nums" }}>
                                        {formatPercent(margin)}
                                    </MDTypography>
                                </MDBox>
                                <MDBox sx={{ height: 8, borderRadius: 4, backgroundColor: FIN_UI.track, overflow: "hidden" }}>
                                    <MDBox sx={{ width: `${marginWidth}%`, height: "100%", backgroundColor: tone.bar, borderRadius: 4 }} />
                                </MDBox>
                                <MDTypography sx={{ fontSize: 11, color: FIN_UI.muted, mt: 1 }}>
                                    Profit = Total Selling Rate − Total Cost
                                </MDTypography>
                            </MDBox>
                        </MDBox>
                    </MDBox>
                </Grid>
            </Grid>
        </MDBox>
    );
}
